import React, { useLayoutEffect, useState } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { Case, Show, Switch } from "control-flow-react";
import PropTypes from "kf-proptypes/widgets";

import { Progress } from "@widgets/feedback/progress/index.js";

import styles from "./styles.css";

export const PROGRESS_INDICATOR_TYPES = {
  TYPE1: "type1",
  TYPE2: "type2"
};

export const PROGRESS_INDICATOR_OVERLAY_COLOR = {
  GRAY: "--kf-color-overlay-gray",
  GRAY_50: "--kf-color-overlay-gray-50",
  GRAY_75: "--kf-color-overlay-gray-75"
};

const STATUS = {
  NOT_STARTED: "notstarted",
  INPROGRESS: "inprogress",
  COMPLETED: "completed",
  ERROR: "error"
};

/**
 * We can use this to show the status or progress of the tasks/process
 */
export function ProgressIndicator(props) {
  const {
    type = PROGRESS_INDICATOR_TYPES.TYPE1,
    title,
    description,
    progress = 0,
    progressText = (progress) => `${progress}%`,
    autoIncrement = false,
    statusMessage,
    errorMessage,
    animate = false,
    getContainer = () => document.body,
    overlayColor = PROGRESS_INDICATOR_OVERLAY_COLOR.GRAY,
    width = "50rem",
    onProgressComplete = () => {}
  } = props;
  const [progressVal, setProgressVal] = useState(progress);

  useLayoutEffect(
    function updateProgress() {
      let intervalRef;
      if (autoIncrement && progress > 0 && progress < 100) {
        setProgressVal((val) => Math.max(progress, val));
        intervalRef = setInterval(() => {
          if (progress) setProgressVal((val) => Math.min(90, val + 1));
        }, 2000);
      }
      return () => intervalRef && clearInterval(intervalRef);
    },
    [progress, autoIncrement]
  );

  function getStatus(progress) {
    if (errorMessage) {
      return STATUS.ERROR;
    } else if (progress > 0 && progress < 100) {
      return STATUS.INPROGRESS;
    } else if (progress > 99) {
      onProgressComplete();
      return STATUS.COMPLETED;
    } else {
      return STATUS.NOT_STARTED;
    }
  }

  const status = getStatus(progress);

  const progressIndicatorWrapperClassName = classNames(
    styles.progressIndicatorWrapper,
    styles[type]
  );

  const progressIndicatorClassName = classNames(
    styles.progressIndicator,
    styles[status]
  );

  const progressBarClassName = classNames(styles.progressBar, {
    [styles.animate]: animate
  });

  const titleClassName = classNames(
    {
      smallplus: type === PROGRESS_INDICATOR_TYPES.TYPE1,
      base600: type === PROGRESS_INDICATOR_TYPES.TYPE2
    },
    styles.title
  );

  const container = getContainer();

  function renderProgressIndicator() {
    return (
      <div className={progressIndicatorWrapperClassName}>
        <Show when={type === PROGRESS_INDICATOR_TYPES.TYPE2}>
          <div
            className={styles.overlay}
            style={{ backgroundColor: `var(${overlayColor})` }}
          />
        </Show>

        <div className={progressIndicatorClassName} style={{ width: width }}>
          <div className={styles.progressMessage}>
            <Show when={title}>
              <div className={titleClassName}>{title}</div>
            </Show>
            <Show when={description}>
              <div className={`tiny400 ${styles.description}`}>
                {description}
              </div>
            </Show>
            <div className={`tiny ${styles.errorMessage}`}>{errorMessage}</div>
          </div>
          <div className={`tiny ${styles.statusMessage}`}>{statusMessage}</div>

          <Switch when={type}>
            <Case value={PROGRESS_INDICATOR_TYPES.TYPE1}>
              <div
                className={styles.progress}
                style={{ width: `${autoIncrement ? progressVal : progress}%` }}
              >
                <div className={styles.progressBg} />
                <div className={progressBarClassName} />
              </div>
            </Case>
            <Case value={PROGRESS_INDICATOR_TYPES.TYPE2}>
              <div className={styles.progressWithText}>
                <Progress
                  percent={autoIncrement ? progressVal : progress}
                  strokeWidth={4}
                  strokeColor={"var(--kf-color-secondary-one-500)"}
                  trailColor={"var(--kf-color-gray-200)"}
                />
                <div className={`tiny ${styles.progressCount}`}>
                  {progressText(autoIncrement ? progressVal : progress)}
                </div>
              </div>
            </Case>
          </Switch>
        </div>
      </div>
    );
  }

  if (type === PROGRESS_INDICATOR_TYPES.TYPE2 && container)
    return ReactDOM.createPortal(renderProgressIndicator(), container);

  return renderProgressIndicator();
}

ProgressIndicator.propTypes = {
  /** Type of the	ProgressIndicator */
  type: PropTypes.oneOf(Object.values(PROGRESS_INDICATOR_TYPES)),
  /** Title showed in ProgressIndicator	 */
  title: PropTypes.string,
  /** Description ProgressIndicator in ProgressIndicator	 */
  description: PropTypes.string,
  /** prpgress Value. it must be in percentage from 0 to 100	 */
  progress: PropTypes.number,
  /** If it is true, Progress automatically increase without progress value change	 */
  autoIncrement: PropTypes.bool,
  /** It is only for type1	 */
  statusMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** It is only for type1. Error message showed in ProgressIndicator	 */
  errorMessage: PropTypes.string,
  /** it for gradient effect in the loader, for now it is only for type1	 */
  animate: PropTypes.bool,
  /** it is overlay color, for now there is three color	 */
  overlayColor: PropTypes.oneOf(
    Object.values(PROGRESS_INDICATOR_OVERLAY_COLOR)
  ),
  /** width of ProgressIndicator */
  width: PropTypes.string,
  /** It is Callback. It will call when progress Completes */
  onProgressComplete: PropTypes.func,
  /** It is applicable for type 2.. by default it is % */
  progressText: PropTypes.func,
  /** Function that returns the DOM container for the ProgressIndicator. */
  getContainer: PropTypes.func
};
